import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { useI18n } from '../../context/I18nContext';
import { useToast } from '../../context/ToastContext';
import Button from '../ui/Button';
import { DEMO_READONLY_EVENT } from '../../services/dataScope';
import { clearDemoWorkspace } from '../../services/demoWorkspace';
import { trackUmamiEvent } from '../../utils/umami';

const TOUR_STORAGE_KEY = 'leadsme:demoTourStep';
const WELCOME_STORAGE_KEY = 'leadsme:demoTourWelcomeDone';

/** Above BulkActionsBar (100) and ContextMenu (2000/2001). */
const DEMO_DOCK_Z = 'z-[2100]';
const DEMO_WELCOME_Z = 'z-[2200]';

export const DEMO_TOUR_STEPS = [
  { id: 'dashboard', path: '/app', titleKey: 'demoTour.stepDashboard', bodyKey: 'demoTour.stepDashboardBody' },
  { id: 'calendar', path: '/app/calendar', titleKey: 'demoTour.stepCalendar', bodyKey: 'demoTour.stepCalendarBody' },
  { id: 'leads', path: '/app/leads', titleKey: 'demoTour.stepLeads', bodyKey: 'demoTour.stepLeadsBody' },
  { id: 'messages', path: '/app/messages', titleKey: 'demoTour.stepMessages', bodyKey: 'demoTour.stepMessagesBody' },
  { id: 'projects', path: '/app/projects', titleKey: 'demoTour.stepProjects', bodyKey: 'demoTour.stepProjectsBody' },
  { id: 'finances', path: '/app/finances', titleKey: 'demoTour.stepFinances', bodyKey: 'demoTour.stepFinancesBody' },
] as const;

function readStep(): number {
  try {
    const raw = sessionStorage.getItem(TOUR_STORAGE_KEY);
    const n = raw == null ? 0 : Number(raw);
    if (!Number.isFinite(n) || n < 0) return 0;
    return Math.min(n, DEMO_TOUR_STEPS.length - 1);
  } catch {
    return 0;
  }
}

function writeStep(step: number) {
  try {
    sessionStorage.setItem(TOUR_STORAGE_KEY, String(step));
  } catch {
    // ignore
  }
}

function clearStep() {
  try {
    sessionStorage.removeItem(TOUR_STORAGE_KEY);
  } catch {
    // ignore
  }
}

function readWelcomeDone(): boolean {
  try {
    return sessionStorage.getItem(WELCOME_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

function writeWelcomeDone() {
  try {
    sessionStorage.setItem(WELCOME_STORAGE_KEY, '1');
  } catch {
    // ignore
  }
}

function clearWelcomeDone() {
  try {
    sessionStorage.removeItem(WELCOME_STORAGE_KEY);
  } catch {
    // ignore
  }
}

function stepIndexForPath(pathname: string): number {
  for (let i = DEMO_TOUR_STEPS.length - 1; i >= 0; i--) {
    const stepPath = DEMO_TOUR_STEPS[i].path;
    if (stepPath === '/app') {
      if (pathname === '/app' || pathname === '/app/') return i;
      continue;
    }
    if (pathname === stepPath || pathname.startsWith(`${stepPath}/`)) return i;
  }
  return -1;
}

interface DemoTourBarProps {
  active: boolean;
}

/**
 * Welcome overlay then bottom coach. Once the user accepts the demo, the tour stays visible until they create their workspace.
 */
const DemoTourBar: React.FC<DemoTourBarProps> = ({ active }) => {
  const { t } = useI18n();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  // Read storage eagerly — useState(false) + useEffect flashes the welcome modal on remount.
  const [welcomeDone, setWelcomeDone] = useState(() => (active ? readWelcomeDone() : false));
  const [playDockAnim, setPlayDockAnim] = useState(false);

  useEffect(() => {
    if (!active) {
      clearStep();
      // Keep WELCOME_STORAGE_KEY so a remount / brief active flicker does not re-show the modal.
      setWelcomeDone(false);
      setPlayDockAnim(false);
      return;
    }
    setWelcomeDone(readWelcomeDone());
    const fromPath = stepIndexForPath(location.pathname);
    const next = fromPath >= 0 ? fromPath : readStep();
    setStep(next);
    writeStep(next);
  }, [active, location.pathname]);

  useEffect(() => {
    if (!active) return;
    const onReadonly = () => showToast(t('demoTour.readonlyToast'), 'info');
    window.addEventListener(DEMO_READONLY_EVENT, onReadonly);
    return () => window.removeEventListener(DEMO_READONLY_EVENT, onReadonly);
  }, [active, showToast, t]);

  if (!active) return null;

  const startTour = () => {
    trackUmamiEvent('demo_welcome_accept');
    trackUmamiEvent('demo_step_view', { step: 0, name: DEMO_TOUR_STEPS[0].id });
    writeWelcomeDone();
    setPlayDockAnim(true);
    setWelcomeDone(true);
    setStep(0);
    writeStep(0);
    navigate(DEMO_TOUR_STEPS[0].path);
  };

  const handleCreateWorkspace = async (from: 'welcome_skip' | 'tour_end') => {
    try {
      setBusy(true);
      trackUmamiEvent('demo_create_workspace', { from });
      await clearDemoWorkspace();
      clearStep();
      clearWelcomeDone();
      showToast(t('demoTour.createdToast'), 'success');
      window.location.assign('/app');
    } catch (e) {
      console.error(e);
      showToast(t('demoTour.actionError'), 'error');
      setBusy(false);
    }
  };

  if (!welcomeDone) {
    return (
      <div
        className={`fixed inset-0 ${DEMO_WELCOME_Z} flex items-center justify-center p-4 md:pl-24`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="demo-welcome-title"
        aria-describedby="demo-welcome-body"
      >
        <div className="absolute inset-0 bg-slate-900/45 backdrop-blur-sm" aria-hidden />
        <div className="relative w-full max-w-md rounded-2xl border border-primary-200 bg-white shadow-2xl shadow-slate-900/20 ring-1 ring-primary-500/20">
          <div className="px-6 py-7 sm:px-8 sm:py-8 text-center">
            <h2 id="demo-welcome-title" className="text-2xl sm:text-3xl font-bold tracking-tight text-primary-600">
              {t('demoTour.welcomeTitle')}
            </h2>
            <p className="mt-2 text-lg font-medium text-slate-900">
              {t('demoTour.welcomeSubtitle')}
            </p>
            <p id="demo-welcome-body" className="mt-3 text-base text-slate-600 leading-relaxed">
              {t('demoTour.welcomeBody')}
            </p>
            <div className="mt-6 flex flex-col gap-2.5">
              <Button
                type="button"
                variant="primary"
                size="lg"
                className="w-full"
                icon={<ArrowRight className="w-4 h-4" />}
                onClick={startTour}
                disabled={busy}
              >
                {t('demoTour.welcomeYes')}
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="md"
                className="w-full"
                loading={busy}
                disabled={busy}
                onClick={() => handleCreateWorkspace('welcome_skip')}
              >
                {t('demoTour.welcomeNo')}
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const isLast = step >= DEMO_TOUR_STEPS.length - 1;
  const current = DEMO_TOUR_STEPS[step];

  const goToStep = (next: number) => {
    const clamped = Math.max(0, Math.min(next, DEMO_TOUR_STEPS.length - 1));
    const target = DEMO_TOUR_STEPS[clamped];
    setStep(clamped);
    writeStep(clamped);
    trackUmamiEvent('demo_step_view', { step: clamped, name: target.id });
    navigate(target.path);
  };

  return (
    <div
      className={`fixed ${DEMO_DOCK_Z} pointer-events-none left-0 right-0 bottom-3 md:bottom-5 md:left-20 px-3 sm:px-4 ${
        playDockAnim ? 'animate-demo-dock' : ''
      }`}
      aria-live="polite"
    >
      <div className="pointer-events-auto mx-auto max-w-3xl rounded-2xl border border-primary-200 bg-white shadow-xl shadow-slate-900/10 ring-1 ring-primary-500/20 overflow-hidden">
        <div className="px-5 py-5 sm:px-6 sm:py-5 flex flex-col sm:flex-row sm:items-center gap-3.5 sm:gap-5">
          <div className="min-w-0 flex-1">
            <p className="text-xs font-medium uppercase tracking-wide text-primary-500">
              {t('demoTour.badge')} · {step + 1}/{DEMO_TOUR_STEPS.length}
            </p>
            <p className="text-lg sm:text-xl font-semibold text-slate-900 mt-1 leading-tight">
              {t(current.titleKey)}
            </p>
            <p className="text-sm sm:text-base text-slate-600 mt-1.5 leading-snug">{t(current.bodyKey)}</p>
          </div>

          <div className="flex items-center gap-2 shrink-0 sm:self-center">
            {step > 0 && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                icon={<ArrowLeft className="w-4 h-4" />}
                onClick={() => goToStep(step - 1)}
                disabled={busy}
                aria-label={t('demoTour.back')}
              />
            )}
            {!isLast ? (
              <Button
                type="button"
                variant="primary"
                size="md"
                icon={<ArrowRight className="w-4 h-4" />}
                onClick={() => goToStep(step + 1)}
                disabled={busy}
              >
                {t('demoTour.next')}
              </Button>
            ) : (
              <Button
                type="button"
                variant="primary"
                size="md"
                loading={busy}
                disabled={busy}
                onClick={() => handleCreateWorkspace('tour_end')}
              >
                {t('demoTour.createWorkspace')}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DemoTourBar;
